import { TFunction } from '@if/core/i18n'
import { formatCurrency, formatDate } from '@if/core/utils'
import { CalendarSimulationAction, ECalendarSimulationAction } from '@src/types/loan/loan-simulation'
import {
  CalendarCog,
  CalendarSync,
  FileX,
  HandCoins,
  ListChecks,
  LucideIcon,
  Pencil,
  Receipt,
  RotateCcw,
  Trash2,
} from 'lucide-react'

export const CALENDAR_ACTION_ICON: Record<ECalendarSimulationAction, LucideIcon> = {
  [ECalendarSimulationAction.UpdateFrequency]: CalendarSync,
  [ECalendarSimulationAction.MoveCalendar]: CalendarCog,
  [ECalendarSimulationAction.Reset]: RotateCcw,
  [ECalendarSimulationAction.EditPayments]: Pencil,
  [ECalendarSimulationAction.ChangePaymentStatus]: ListChecks,
  [ECalendarSimulationAction.AddFee]: Receipt,
  [ECalendarSimulationAction.EditFees]: Pencil,
  [ECalendarSimulationAction.ChangeRegularPmt]: HandCoins,
  [ECalendarSimulationAction.ChargeOff]: FileX,
  [ECalendarSimulationAction.DeleteChargeOff]: Trash2,
}

export interface CalendarActionDescription {
  /** What the action did, in one line. */
  label: string
  /** What it touched, when a count says more than the line does. */
  detail?: string
}

/**
 * One line per action in the change log, written from the action itself rather than from a
 * diff of the calendars around it: an action carries what the agent asked for, and that is
 * what they recognize when deciding how far back to undo.
 */
export const describeCalendarAction = (
  action: CalendarSimulationAction,
  t: TFunction<'translation'>,
): CalendarActionDescription => {
  switch (action.actionType) {
    case ECalendarSimulationAction.UpdateFrequency:
      return {
        label: t('loan.calendar.changeLog.actions.updateFrequency', {
          frequency: t(`EPaymentFrequency.${action.frequency}`, { ns: 'enum', defaultValue: action.frequency }),
        }),
        detail: formatDate(action.firstPaymentDate),
      }

    case ECalendarSimulationAction.MoveCalendar:
      return {
        label: t('loan.calendar.changeLog.actions.moveCalendar', {
          fromDate: formatDate(action.fromDate),
          toDate: formatDate(action.toDate),
        }),
      }

    case ECalendarSimulationAction.Reset:
      return { label: t('loan.calendar.changeLog.actions.reset') }

    case ECalendarSimulationAction.EditPayments:
      return {
        label: t('loan.calendar.changeLog.actions.editPayments'),
        detail: [
          action.editedPayments.length > 0 &&
            t('loan.payment.editPayments.log.changed', { count: action.editedPayments.length }),
          action.newPayments.length > 0 &&
            t('loan.payment.editPayments.log.added', { count: action.newPayments.length }),
          action.deletedPaymentIds.length > 0 &&
            t('loan.payment.editPayments.log.removed', { count: action.deletedPaymentIds.length }),
        ]
          .filter(Boolean)
          .join(' · '),
      }

    case ECalendarSimulationAction.ChangePaymentStatus:
      return {
        label: t('loan.calendar.changeLog.actions.changePaymentStatus'),
        detail: t('loan.calendar.changeLog.payments', { count: action.paymentIds.length }),
      }

    case ECalendarSimulationAction.AddFee:
      return {
        label: t('loan.calendar.changeLog.actions.addFee', { amount: formatCurrency(action.amount) }),
        detail: formatDate(action.effectiveDate),
      }

    case ECalendarSimulationAction.EditFees:
      if (action.reappliedFeeIds.length > 0)
        return { label: t('loan.calendar.changeLog.actions.reapplyFees', { count: action.reappliedFeeIds.length }) }
      return action.editedFees.length > 0
        ? { label: t('loan.calendar.changeLog.actions.editFees', { count: action.editedFees.length }) }
        : { label: t('loan.calendar.changeLog.actions.cancelFees', { count: action.cancelledFeeIds.length }) }

    case ECalendarSimulationAction.ChangeRegularPmt:
      return {
        label: t('loan.calendar.changeLog.actions.changeRegularPmt', { amount: formatCurrency(action.amount) }),
        detail: formatDate(action.fromDate),
      }

    case ECalendarSimulationAction.ChargeOff:
      return {
        label: t('loan.calendar.changeLog.actions.chargeOff'),
        detail: formatDate(action.effectiveDate),
      }

    case ECalendarSimulationAction.DeleteChargeOff:
      return { label: t('loan.calendar.changeLog.actions.deleteChargeOff') }
  }
}
